import { SignupCountry } from '../../types/merchantSignup/MerchantSignup';

/**
 * Client-side rules of the merchant signup page: organization number format and checksum, postal code format and
 * phone example per country, plus website, phone, email and name syntax. The API re-validates everything and
 * normalizes websites to https:// and phone numbers to E.164, so these only exist for fast feedback in the form.
 */

interface CountryRules {
    /** Shown as the organization number placeholder. */
    organizationNumberExample: string;
    organizationNumberDigits: number;
    hasValidOrganizationChecksum: (digits: string) => boolean;
    formatOrganizationNumber: (digits: string) => string;
    /** Shown as the postal code placeholder. */
    postalCodeExample: string;
    postalCodeDigits: number;
    formatPostalCode: (digits: string) => string;
    /** Shown as the phone number placeholder. */
    phoneExample: string;
    /** The country calling code, put in front of a phone number typed without one. */
    phoneCountryCode: string;
}

const digitsOf = (value: string) => value.replace(/\D/g, '');

/** Luhn (mod 10) over every digit, the last one being the check digit. */
const isLuhnValid = (digits: string): boolean => {
    let sum = 0;

    for (let i = 0; i < digits.length; i += 1) {
        let digit = Number(digits[digits.length - 1 - i]);

        if (i % 2 === 1) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }

        sum += digit;
    }

    return sum % 10 === 0;
};

/**
 * Mod 11 with the given weights over the digits before the check digit: the check digit is 11 minus the remainder,
 * 0 when the remainder is 0, and a remainder of 1 makes the number invalid (the check digit would have to be 10).
 */
const isMod11Valid = (digits: string, weights: number[]): boolean => {
    const sum = weights.reduce((total, weight, index) => total + weight * Number(digits[index]), 0);
    const remainder = sum % 11;
    const expected = remainder === 0 ? 0 : 11 - remainder;

    return expected !== 10 && Number(digits[weights.length]) === expected;
};

const rules: Record<SignupCountry, CountryRules> = {
    SE: {
        organizationNumberExample: '556677-8899',
        organizationNumberDigits: 10,
        hasValidOrganizationChecksum: isLuhnValid,
        formatOrganizationNumber: d => `${d.slice(0, 6)}-${d.slice(6)}`,
        postalCodeExample: '111 22',
        postalCodeDigits: 5,
        formatPostalCode: d => `${d.slice(0, 3)} ${d.slice(3)}`,
        phoneExample: '+46 70 123 45 67',
        phoneCountryCode: '46',
    },
    NO: {
        organizationNumberExample: '123 456 789',
        organizationNumberDigits: 9,
        hasValidOrganizationChecksum: d => isMod11Valid(d, [3, 2, 7, 6, 5, 4, 3, 2]),
        formatOrganizationNumber: d => `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`,
        postalCodeExample: '0154',
        postalCodeDigits: 4,
        formatPostalCode: d => d,
        phoneExample: '+47 912 34 567',
        phoneCountryCode: '47',
    },
    FI: {
        organizationNumberExample: '1234567-8',
        organizationNumberDigits: 8,
        hasValidOrganizationChecksum: d => isMod11Valid(d, [7, 9, 10, 5, 8, 4, 2]),
        formatOrganizationNumber: d => `${d.slice(0, 7)}-${d.slice(7)}`,
        postalCodeExample: '00100',
        postalCodeDigits: 5,
        formatPostalCode: d => d,
        phoneExample: '+358 40 123 4567',
        phoneCountryCode: '358',
    },
    DK: {
        organizationNumberExample: '12345678',
        organizationNumberDigits: 8,
        hasValidOrganizationChecksum: d => isMod11Valid(d, [2, 7, 6, 5, 4, 3, 2]),
        formatOrganizationNumber: d => d,
        postalCodeExample: '1160',
        postalCodeDigits: 4,
        formatPostalCode: d => d,
        phoneExample: '+45 20 12 34 56',
        phoneCountryCode: '45',
    },
};

export const getOrganizationNumberExample = (country: SignupCountry) =>
    rules[country].organizationNumberExample;

export const getPostalCodeExample = (country: SignupCountry) => rules[country].postalCodeExample;

export const getPhoneExample = (country: SignupCountry) => rules[country].phoneExample;

/** Strips spaces and dashes so a number can be validated and sent regardless of how it was typed. */
export const normalizeOrganizationNumber = (organizationNumber: string) =>
    organizationNumber.replace(/[\s-]/g, '');

/** Right number of digits and a valid checksum for the country. Only digits are expected after normalizing. */
export const isOrganizationNumberValid = (country: SignupCountry, organizationNumber: string) => {
    const normalized = normalizeOrganizationNumber(organizationNumber);
    const { organizationNumberDigits, hasValidOrganizationChecksum } = rules[country];

    return (
        /^\d+$/.test(normalized) &&
        normalized.length === organizationNumberDigits &&
        hasValidOrganizationChecksum(normalized)
    );
};

/** The organization number in the display format of the country, or as typed when it is not complete. */
export const formatOrganizationNumber = (country: SignupCountry, organizationNumber: string) => {
    const digits = digitsOf(organizationNumber);

    return digits.length === rules[country].organizationNumberDigits
        ? rules[country].formatOrganizationNumber(digits)
        : organizationNumber;
};

export const normalizePostalCode = (postalCode: string) => postalCode.replace(/[\s-]/g, '');

/** The number of digits of the country (SE and FI five, NO and DK four), spaces and dashes ignored. */
export const isPostalCodeValid = (country: SignupCountry, postalCode: string) => {
    const normalized = normalizePostalCode(postalCode);

    return /^\d+$/.test(normalized) && normalized.length === rules[country].postalCodeDigits;
};

/** The postal code in the display format of the country (SE 111 22), or as typed when it is not valid. */
export const formatPostalCode = (country: SignupCountry, postalCode: string) =>
    isPostalCodeValid(country, postalCode)
        ? rules[country].formatPostalCode(normalizePostalCode(postalCode))
        : postalCode;

/** Digits, spaces and dashes with an optional leading +, and at least seven digits. */
export const isPhoneValid = (phone: string) => {
    const trimmed = phone.trim();

    return /^\+?[\d\s-]+$/.test(trimmed) && digitsOf(trimmed).length >= 7;
};

/**
 * The phone number with the calling code of the country in front when it was typed without one, as the number is
 * dialled from abroad: a leading 00 becomes +, and a national number loses its trunk prefix (the leading 0, as in
 * 070-123 45 67) before the code is added. A number that already starts with + and a number that is not valid are
 * left as typed, apart from trimming.
 */
export const normalizePhone = (country: SignupCountry, phone: string) => {
    const trimmed = phone.trim();

    if (!isPhoneValid(trimmed) || trimmed.startsWith('+')) return trimmed;

    if (trimmed.startsWith('00')) return `+${trimmed.slice(2).trimStart()}`;

    const national = trimmed.replace(/^0+[\s-]*/, '');

    return `+${rules[country].phoneCountryCode} ${national}`;
};

/** A domain with a valid top-level domain; https:// is optional. */
export const isWebsiteValid = (website: string) =>
    /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(website.trim());

/** The website with https:// in front when no scheme was given, as the API stores it. */
export const normalizeWebsite = (website: string) => {
    const trimmed = website.trim();

    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const isEmailValid = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

/** At least two words. */
export const isFullNameValid = (name: string) => name.trim().split(/\s+/).length >= 2;

export const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] ?? '';
